import styled from 'astroturf/react'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { parseQuery } from '@calendly/http-tools'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useParams, useRouteMatch } from 'react-router-dom'
import { Calendar, DatesTitle as Title, CalendarContainer, DatesWrapper as Wrapper } from '@calendly/booking-kit'
import { useCallback } from 'react'

import { selectCalendarDay } from 'actions/calendar_day'
import { removeSpotFromCheckQueue } from 'actions/spot'
import { changeCalendarMonth } from 'actions/calendar'
import { eventTypeSelector } from 'root-api'
import {
  monthSelector,
  nextMonthAvailableSelector,
  todaySelector,
  activeDayIdsSelector,
  loadingProgressSelector
} from 'selectors'
import { startOfTheWeekSelector } from 'selectors/week'
import { isDiagnosticsEnabledSelector } from 'selectors/diagnostics'
import { calendarWithParsedDaySelector, daySelector, hideEventTypeDetailsSelector } from 'selectors/calendar'
import { useFeatures } from 'components/use_organization'
import { useFlag } from '@calendly/feature-check-react'

import { BookingRedesignBackLink } from 'components/booking_redesign_back_link'
import { buildProfileEventTypesListUrl } from 'utils/booking_profile_back_url'

import Timezone from '../timezone'
import _CalendarBackRow from "dates-CalendarBackRow.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/dates.js?styleId=CalendarBackRow";
import _StyledCalendar from "dates-StyledCalendar.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/dates.js?styleId=StyledCalendar";


const CalendarBackRow = /*#__PURE__*/styled('div', null, {
  displayName: "CalendarBackRow",
  styles: _CalendarBackRow,
  attrs: props => ({
    ...props,
    isPhone: useDeviceType('isPhone')
  })
})

const StyledCalendar = /*#__PURE__*/styled(Calendar, null, {
  displayName: "StyledCalendar",
  styles: _StyledCalendar,
  attrs: props => ({
    ...props,
    isPhone: useDeviceType('isPhone')
  })
})

const Dates = () => {
  const dispatch = useDispatch()
  const { search } = useLocation()
  const { profileSlug } = useParams()
  const { path } = useRouteMatch()
  const calendar = useSelector(calendarWithParsedDaySelector)
  const today = useSelector(todaySelector)
  const activeDayIds = useSelector(activeDayIdsSelector)
  const isLoading = useSelector(loadingProgressSelector) < 100
  const diagnosticsEnabled = useSelector(isDiagnosticsEnabledSelector)
  const month = useSelector(monthSelector)
  const weekStartDay = useSelector(startOfTheWeekSelector)
  const day = useSelector(daySelector)
  const hasNoSidebar = useSelector(hideEventTypeDetailsSelector)
  const isPhone = useDeviceType('isPhone')
  const isTabletUp = useDeviceType('isTabletUp')
  const eventType = useSelector(eventTypeSelector)
  const {
    availability_timezone: eventTypeTimezone,
    period_type: eventTypePeriodType,
    start_date: eventTypeStartDate
  } = eventType || {}
  const theme = parseQuery(window.location.search)

  const { data } = useFeatures()
  const hasCustomTheme = Boolean(theme.primary_color) && Boolean(!data || data.custom_theme_allowed)

  const isNextMonthAvailable = useSelector(nextMonthAvailableSelector)
  const bookingAppRedesign = useFlag('booking_app_redesign', false)
  // Redesign puts the picker in the times column header on tablet+; skip the duplicate under the calendar.
  const showTimezoneBelowCalendar =
    !bookingAppRedesign || (!!day && !isTabletUp)

  const { back } = parseQuery(search)
  const shouldShowProfileBackButton = Boolean(
    eventType
    && parseInt(back, 10) === 1
    && eventType.public
    && path === '/:profileSlug/:eventTypeSlug'
  )
  const showCalendarRedesignBack = bookingAppRedesign && shouldShowProfileBackButton
  const profileBackHref = showCalendarRedesignBack && profileSlug
    ? buildProfileEventTypesListUrl(profileSlug, search)
    : undefined

  const onSelectDay = useCallback((dayToSelect, selectedSpot, isFromKeyboard) => {
    dispatch(selectCalendarDay(dayToSelect, isFromKeyboard))
    dispatch(removeSpotFromCheckQueue(selectedSpot))
  }, [dispatch])

  const onClickPrevMonth = useCallback(() => {
    dispatch(changeCalendarMonth(-1))
  }, [dispatch])

  const onClickNextMonth = useCallback(() => {
    dispatch(changeCalendarMonth(1))
  }, [dispatch])

  return (
    <Wrapper hasSelectedDay={!!day} data-component="spotpicker-dates">
      {showCalendarRedesignBack && profileBackHref && !isPhone && (
        <CalendarBackRow>
          <BookingRedesignBackLink href={profileBackHref} />
        </CalendarBackRow>
      )}
      {isPhone && <Title compact={hasNoSidebar} />}
      <CalendarContainer>
        <StyledCalendar
          calendar={calendar}
          today={today}
          activeDayIds={activeDayIds}
          isLoading={isLoading}
          diagnosticsEnabled={diagnosticsEnabled}
          hasCustomTheme={hasCustomTheme}
          month={month}
          weekStartDay={weekStartDay}
          compact={isPhone && hasNoSidebar}
          eventTypeTimezone={eventTypeTimezone}
          eventTypePeriodType={eventTypePeriodType}
          eventTypeStartDate={eventTypeStartDate}
          isNextMonthAvailable={isNextMonthAvailable}
          onClickPrevMonth={onClickPrevMonth}
          onClickNextMonth={onClickNextMonth}
          onSelectDay={onSelectDay}
        />
        {showTimezoneBelowCalendar && (
          <Timezone variant={bookingAppRedesign ? 'minimal' : 'default'} />
        )}
      </CalendarContainer>
    </Wrapper>
  )
}

export default Dates
